/**
 * Read-only enforcement for Super Admin Support Mode.
 *
 * The guard is installed ONLY while a read-only support session is active and
 * removed the moment editing is enabled or the session ends, so normal users
 * never execute any of this code (zero performance impact).
 *
 * This is a client-side safety net. Row Level Security remains the final
 * authority and is not modified by this feature.
 */
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const WRITE_METHODS = new Set(["insert", "update", "upsert", "delete"]);
const STORAGE_WRITE_METHODS = new Set([
  "upload",
  "uploadToSignedUrl",
  "update",
  "remove",
  "move",
  "copy",
  "createSignedUploadUrl",
]);

/** Tables the support feature itself must always be able to write to (its own audit trail). */
const AUDIT_TABLES = new Set(["support_sessions", "support_session_events"]);

/** Edge functions that are safe (read-only) and must keep working in read-only mode. */
const SAFE_FUNCTIONS = new Set([
  "get-google-maps-key",
  "get-maps-key",
  "photo-proxy",
  "get-signed-url",
  "postcode-lookup",
  "dvla-lookup",
]);

const READ_ONLY_ERROR = {
  message: "Support Mode is read only. Enable editing to make changes.",
  details: "",
  hint: "",
  code: "SUPPORT_READ_ONLY",
};

let installed = false;
let originals: {
  from: any;
  storageFrom: any;
  invoke: any;
} | null = null;

let lastToastAt = 0;
function warn() {
  const now = Date.now();
  if (now - lastToastAt < 2500) return;
  lastToastAt = now;
  toast({
    title: "Read only",
    description: "Support Mode is read only. Enable editing to change this customer's data.",
    variant: "destructive",
  });
}

/** A chainable stub that always resolves to a Postgrest-shaped error result. */
function blockedResult(): any {
  const result = { data: null, error: READ_ONLY_ERROR, count: null, status: 403, statusText: "Forbidden" };
  const target: any = function () {
    return proxy;
  };
  const proxy: any = new Proxy(target, {
    get(_t, prop) {
      if (prop === "then") {
        return (resolve: any) => Promise.resolve(result).then(resolve);
      }
      if (prop === "catch" || prop === "finally") {
        return () => proxy;
      }
      return () => proxy;
    },
    apply() {
      return proxy;
    },
  });
  return proxy;
}

export function installSupportReadOnlyGuard() {
  if (installed) return;
  const client: any = supabase;

  originals = {
    from: client.from.bind(client),
    storageFrom: client.storage.from.bind(client.storage),
    invoke: client.functions.invoke.bind(client.functions),
  };

  client.from = (table: string) => {
    const builder = originals!.from(table);
    if (AUDIT_TABLES.has(table)) return builder;
    return new Proxy(builder, {
      get(target, prop, receiver) {
        if (typeof prop === "string" && WRITE_METHODS.has(prop)) {
          return () => {
            warn();
            return blockedResult();
          };
        }
        const value = Reflect.get(target, prop, receiver);
        return typeof value === "function" ? value.bind(target) : value;
      },
    });
  };

  client.storage.from = (bucket: string) => {
    const api = originals!.storageFrom(bucket);
    return new Proxy(api, {
      get(target, prop, receiver) {
        if (typeof prop === "string" && STORAGE_WRITE_METHODS.has(prop)) {
          return async () => {
            warn();
            return { data: null, error: new Error(READ_ONLY_ERROR.message) };
          };
        }
        const value = Reflect.get(target, prop, receiver);
        return typeof value === "function" ? value.bind(target) : value;
      },
    });
  };

  client.functions.invoke = async (name: string, options?: any) => {
    if (SAFE_FUNCTIONS.has(name)) return originals!.invoke(name, options);
    warn();
    return { data: null, error: new Error(READ_ONLY_ERROR.message) };
  };

  installed = true;
}

export function removeSupportReadOnlyGuard() {
  if (!installed || !originals) return;
  const client: any = supabase;
  client.from = originals.from;
  client.storage.from = originals.storageFrom;
  client.functions.invoke = originals.invoke;
  originals = null;
  installed = false;
}

export function isSupportReadOnlyGuardInstalled() {
  return installed;
}
